import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import Cookies from 'js-cookie'
import { Request, REQUEST_MESSAGE_TYPES } from '../schemas/request'
import { useDispatch } from '../redux/store'
import { useSnackbar, SnackbarKey } from 'notistack'
import { setPingTimeout, updatePingInfo } from '../redux/slices/socket-health'
import { generateID } from '../utils/generateMessageID'
import addMessageChunks from '../utils/messageSequence'
import { eventCentral } from '../redux/action-creators/event-central'
import { ConfigContext } from './ConfigurationContext'
import { setAuthenticated } from '../redux/slices/socket-auth-status'
import { isJwtValid } from 'utils/jwt'

type SocketContextType = {
  onSocketSend: (msg: Request) => Promise<boolean>
  onSocketClosed: () => void
  socket: React.MutableRefObject<WebSocket | null>
  reconnectSocket: () => void
  checkWebSocketStatus: () => string
  isAuthenticated: boolean
  getLastPingTime: React.MutableRefObject<number>
  waitForAuthentication: () => Promise<void>
}

export const SocketContext = createContext<SocketContextType | null>(null)

// ✅ Singleton WebSocket container
const staticSocket = {
  instance: null as WebSocket | null,
  initialized: false
}

export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch()
  const config = useContext(ConfigContext)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const socket = useRef<WebSocket | null>(null)
  const lastPingTime = useRef(Date.now())
  const pingInterval = useRef(parseInt(config?.REACT_APP_PING_INTERVAL_IN_MS || '700000'))
  const reconnecting = useRef(false)
  const notificationKey = useRef<SnackbarKey | null>(null)
  const decodedChunks = useRef<Record<string, string>>({})
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const reconnectTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const authCallbackRef = useRef<() => void>(() => {})
  const hasConfirmedAuth = useRef(false)

  const handleTokenExpiry = () => {
    console.warn('[Socket Provider] Token expired. Logging out...')
    Cookies.remove('token')

    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current.close()
    }

    reconnecting.current = false
    staticSocket.initialized = false
    hasConfirmedAuth.current = false
    setIsAuthenticated(false)
    dispatch(setAuthenticated(false))

    window.location.href = '/auth/login' // or navigate('/auth/login') if in component
  }

  function setupSocket(ws: WebSocket) {
    ws.onopen = () => {
      reconnecting.current = false
      lastPingTime.current = Date.now()
      dispatch(updatePingInfo({ lastPingTime: lastPingTime.current, pingLatency: 0 }))

      // 🔍 Table logging WebSocket status
      console.table({
        Time: new Date().toLocaleTimeString(),
        'Socket State': ws.readyState,
        'Static Initialized': staticSocket.initialized,
        'Has Token': !!Cookies.get('token'),
        Reconnecting: reconnecting.current,
        'Has Confirmed Auth': hasConfirmedAuth.current
      })

      if (notificationKey.current) {
        closeSnackbar(notificationKey.current)
        notificationKey.current = null
      }
  
      const token = Cookies.get('token')
      if (token && ws.readyState === WebSocket.OPEN) {
        const msg: Request = {
          messageId: generateID(),
          messageType: REQUEST_MESSAGE_TYPES.Auth,
          Auth: { jwt: token }
        }
        ws.send(JSON.stringify(msg))
      }
    }
  
    ws.onmessage = (event: MessageEvent) => {
      const now = Date.now()
      lastPingTime.current = now
      dispatch(updatePingInfo({ lastPingTime: now, pingLatency: 0 }))
  
      let data
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }

      // ✅ Handle token expiry before anything else
      if (
        data.messageType === 'Status' &&
        (data.Status?.statusCode === 9777 || data.Status?.status === 'TimedOut')
      ) {
        handleTokenExpiry()
        return
      }

      switch (data.messageType) {
        case 'AuthConfirmation':
          setIsAuthenticated(true)
          dispatch(setAuthenticated(true))
          hasConfirmedAuth.current = true
          eventCentral(event.data)
  
          if (authCallbackRef.current) {
            console.log('[Socket Provider] AuthConfirmation received: running registered callback')
            authCallbackRef.current()
          }
          break
  
        case 'MessageSegment32KB': {
          const ref = data.messageIdReference
          decodedChunks.current[ref] = addMessageChunks(data, decodedChunks.current[ref])
          if (
            data?.MessageSegment32KB?.part === data?.MessageSegment32KB?.parts &&
            decodedChunks.current[ref]
          ) {
            eventCentral(decodedChunks.current[ref])
          }
          break
        }
  
        default:
          eventCentral(event.data)
      }
    }
  
    ws.onclose = () => reconnectSocket()
    ws.onerror = () => reconnectSocket()
  }

  const waitForAuthentication = () => {
    return new Promise<void>((resolve) => {
      if (hasConfirmedAuth.current) {
        resolve()
      } else {
        authCallbackRef.current = () => {
          console.log('[Socket Provider] Auth confirmed in waitForAuthentication()')
          resolve()
        }
      }
    })
  }

  const reconnectSocket = useCallback(() => {
    const state = socket.current?.readyState
    console.log('Entering into reconnect realm')
    if (reconnecting.current || state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
      console.log('[Socket Provider] Skipping reconnect, socket already connecting/open.')
      return
    }

    reconnecting.current = true

    if (reconnectTimeoutId.current) {
      console.log('[Socket Provider] Reconnect already scheduled, skipping.')
      return
    }

    if (notificationKey.current) closeSnackbar(notificationKey.current)

    const key = enqueueSnackbar('Connection lost. Reconnecting...', {
      variant: 'warning',
      persist: true
    })
    notificationKey.current = key

    reconnectTimeoutId.current = setTimeout(async () => {
      const token = Cookies.get('token')
      const currentSocket = socket.current

      if (currentSocket?.readyState === WebSocket.OPEN && token) {
        const msg: Request = {
          messageId: generateID(),
          messageType: REQUEST_MESSAGE_TYPES.DeAuth,
          DeAuth: { jwt: token }
        }
        currentSocket.send(JSON.stringify(msg))
        console.log('[Socket Provider] Sent DeAuth before reconnecting.')
        currentSocket.close()
      }

      const url = currentSocket?.url || config.REACT_APP_AWS_SOCKET
      const newSocket = new WebSocket(url)
      socket.current = newSocket
      staticSocket.instance = newSocket
      staticSocket.initialized = true
      reconnectTimeoutId.current = null
      setupSocket(newSocket)
    }, 2000)
  }, [config.REACT_APP_AWS_SOCKET, enqueueSnackbar, closeSnackbar])

  const onSocketSend = async (msg: Request) => {
    await waitForAuthentication()
    if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
      reconnectSocket()
      return false
    }

    socket.current.send(JSON.stringify(msg))
    return true
  }

  const onSocketClosed = () => {
    const token = Cookies.get('token')
    if (socket.current?.readyState === WebSocket.OPEN && token) {
      const msg: Request = {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.DeAuth,
        DeAuth: { jwt: token }
      }
      onSocketSend(msg).then(() => {
        Cookies.remove('token')
        socket.current?.close()
      })
    }
  }


  
  const checkWebSocketStatus = () => {
    return ['Connecting', 'Open', 'Closing', 'Closed'][socket.current?.readyState || 3]
  }

  useEffect(() => {
    if (staticSocket.initialized) return

    let checkInterval: NodeJS.Timeout

    const tryInit = () => {
      const token = Cookies.get('token')
      if (!token) {
        console.log('[Socket Provider] No token yet. Waiting before socket init...')
        return
      }
       console.log('[Socket Provider] Checking token validity')

      if (!isJwtValid()) {
        console.log('[Socket Provider] Skipping socket init — JWT invalid.')
        return
      }

      if (socket.current?.readyState === WebSocket.CONNECTING) {
        console.log('[Socket Provider] Socket is already connecting...')
        return
      }

      if (socket.current?.readyState === WebSocket.OPEN) {
        console.log('[Socket Provider] Socket already open.')
        return
      }

      console.log('[Socket Provider] Token found. Initializing socket...')
      const ws = new WebSocket(config?.REACT_APP_AWS_SOCKET)
      socket.current = ws
      staticSocket.instance = ws
      staticSocket.initialized = true
      setupSocket(ws)
      clearInterval(checkInterval)
    }

    checkInterval = setInterval(tryInit, 500)

    return () => {
      clearInterval(checkInterval)
      if (reconnectTimeoutId.current) {
        clearTimeout(reconnectTimeoutId.current)
        reconnectTimeoutId.current = null
      }
    }
  }, [setupSocket, config?.REACT_APP_AWS_SOCKET])
  

  // 🕑 Centralized ping monitor
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now()
      const diff = now - lastPingTime.current

      console.log('[Ping Timer] Time since last ping:', diff + 'ms')
      dispatch(updatePingInfo({ lastPingTime: lastPingTime.current, pingLatency: diff }))

      if (diff > pingInterval.current) {
        dispatch(setPingTimeout())
        socket.current?.close()
        reconnectSocket()
      }
    }, 5000)

    return () => clearInterval(id)
  }, [dispatch, reconnectSocket])

  return (
    <SocketContext.Provider
      value={{
        onSocketSend,
        onSocketClosed,
        socket,
        reconnectSocket,
        checkWebSocketStatus,
        isAuthenticated,
        getLastPingTime: lastPingTime,
        waitForAuthentication
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}